import { AxiosResponse } from "axios";
import { useFormik } from "formik";
import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import { Checkbox, FormControlLabel, Stack, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";

import { Dialog } from "components/Dialog";
import { REPORT_GENERATE } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { useReportSectionsQuery } from "hooks/queries/print-cases";

export interface Props {
  caseIds: number[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

type CasePrintPayload = { reportTitle: string } & Record<
  string,
  string | boolean
>;
type FormValues = { reportTitle?: string } & Record<string, string | boolean>;

export const PrintCasesDialog: FC<Props> = ({
  caseIds,
  isOpen,
  onClose,
  onConfirm
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const {
    data: reportSections,
    isLoading: isReportSectionsLoading,
    isError: isReportSectionsError
  } = useReportSectionsQuery();

  const initialValues = useMemo(() => {
    if (!isReportSectionsLoading && !isReportSectionsError) {
      return reportSections.results.reduce(
        (acc: FormValues, section) => {
          acc[section.section_id] = section.required;
          return acc;
        },
        { reportTitle: "" }
      );
    }
    return {};
  }, [isReportSectionsLoading, isReportSectionsError, reportSections]);

  // TODO: maybe split out into queries hooks folder
  const printCasesMutation = useMutation({
    mutationFn: async (payload: CasePrintPayload) => {
      const { reportTitle, ...rest } = payload;
      const sections = Object.entries(rest).reduce((acc, entry) => {
        const [key, value] = entry;
        if (value) {
          acc.push(key);
        }
        return acc;
      }, [] as string[]);

      return axios.post<CasePrintPayload, AxiosResponse<Blob>>(
        REPORT_GENERATE,
        {
          cases: caseIds,
          title: reportTitle,
          sections
        },
        {
          responseType: "blob"
        }
      );
    },
    onSuccess: data => {
      addSnackbarToQueue({
        message: "Report successfully created.",
        severity: "success"
      });

      const isoDate = new Date().toISOString().split("T")[0];

      const objectUrl = window.URL.createObjectURL(data.data);
      const anchor = document.createElement("a");
      document.body.appendChild(anchor);
      anchor.download = `case-mgmt-report-${isoDate}.pdf`;
      anchor.target = "_blank";
      anchor.href = objectUrl;
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);

      onClose();
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to create report.",
        severity: "error"
      });
    }
  });

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: CasePrintPayload,
      reset: () => void
    ): Promise<void> => {
      if (!isValid || isReportSectionsLoading || isReportSectionsError) {
        return;
      }
      await printCasesMutation.mutate(values);
      onConfirm();
      reset();
    },
    [
      isReportSectionsError,
      isReportSectionsLoading,
      onConfirm,
      printCasesMutation
    ]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: values => {
      handleSubmit(formik.isValid, values as CasePrintPayload, () => {});
    }
  });

  const closeAndResetDialog = (): void => {
    onClose();

    setTimeout(() => {
      formik.setErrors({});
      formik.resetForm();
    }, 50);
  };

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.setErrors({});
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loading = printCasesMutation.isLoading;

  return (
    <Dialog
      dialogId="print-cases-dialog"
      title="Print Cases"
      open={isOpen}
      size="small"
      fullWidth={true}
      onClose={closeAndResetDialog}
      data-qa="CM-Print-Cases-Dialog"
      confirmDisabled={loading}
      confirmText="Submit"
      onSubmit={event => {
        event.preventDefault();
        formik.submitForm();
      }}
    >
      <StyledDialogContent>
        <ProgressIndicatorContainer
          loading={isReportSectionsLoading}
          error={isReportSectionsError}
          errorMessage="An error occurred"
        >
          {!isReportSectionsLoading &&
            !isReportSectionsError &&
            Object.keys(formik.values).length && (
              <Stack direction="column" gap={2}>
                <TextField
                  variant="outlined"
                  fullWidth
                  autoFocus
                  name="reportTitle"
                  id="reportTitle"
                  label="Report Title"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.reportTitle &&
                    Boolean(formik.errors.reportTitle)
                  }
                  helperText={
                    formik.touched.reportTitle && formik.errors.reportTitle
                  }
                  value={formik.values.reportTitle}
                />

                <Stack direction="column" alignItems="flex-start">
                  {reportSections?.results.map(section => (
                    <FormControlLabel
                      key={section.section_id}
                      label={section.display}
                      labelPlacement="end"
                      name={section.section_id}
                      checked={formik.values[section.section_id] as boolean}
                      required={section.required}
                      value={formik.values[section.section_id]}
                      onChange={
                        !section.required ? formik.handleChange : undefined
                      }
                      onBlur={formik.handleBlur}
                      control={
                        <Checkbox
                          size="small"
                          inputProps={{ readOnly: section.required }}
                        />
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            )}
        </ProgressIndicatorContainer>
      </StyledDialogContent>
    </Dialog>
  );
};

const StyledDialogContent = styled("div")`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;
